import { useEffect, useState, useRef } from 'react';
import { ImagePlaceholder } from './imagePlaceholder';
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from '~/lib/utils';

interface ImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'width' | 'height'> {
  src: string | undefined;
  fallbackWidth?: number;
  fallbackHeight?: number;
  containerClassName?: string;
  rotation?: number;
  scaleOnRotate?: boolean;
  /** When true, image loads immediately without lazy loading. Use for LCP images (hero, above-the-fold). */
  priority?: boolean;
}

function extractDimensions(url: string): { width: number; height: number } | null {
  const match = url?.match(/\/(\d+)x(\d+)\//);
  if (match) {
    return {
      width: parseInt(match[1], 10),
      height: parseInt(match[2], 10)
    };
  }
  return null;
}

export function Image({
  src,
  alt = '',
  className,
  containerClassName,
  fallbackWidth = 300,
  fallbackHeight = 200,
  rotation = 0,
  scaleOnRotate = false,
  priority = false,
  ...props
}: ImageProps) {
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(priority); // Priority images load immediately
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Reset loading state when src changes
  useEffect(() => {
    setIsLoading(true);
    setError(false);
  }, [src]);

  // Check if image is already loaded (from cache)
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img?.naturalWidth > 0) {
      setIsLoading(false);
    }
  }, [src, shouldLoad]);

  // Sync shouldLoad with priority prop changes
  useEffect(() => {
    if (priority) {
      setShouldLoad(true);
    }
  }, [priority]);

  useEffect(() => {
    // Skip intersection observer for priority images or before mount
    if (!isMounted || priority) return;

    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        setIsInView(entry.isIntersecting);
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.unobserve(container);
        }
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    observer.observe(container);

    return () => {
      observer.unobserve(container);
    };
  }, [isMounted, src, priority]);

  const dimensions = src ? extractDimensions(src) : null;
  const width = dimensions?.width ?? fallbackWidth;
  const height = dimensions?.height ?? fallbackHeight;

  // Calculate aspect ratio to reserve space and prevent CLS
  const aspectRatio = width / height;

  // Show placeholder/error states
  const showPlaceholder = !src || error;
  
  return (
    <div
      ref={containerRef}
      className={cn(
        "relative w-full overflow-hidden",
        containerClassName
      )}
      style={{ aspectRatio }}
    >
      {/* Placeholder for missing src or error */}
      {showPlaceholder && (
        <div className="absolute inset-0">
          <ImagePlaceholder
            width={width}
            height={height}
            className="w-full h-full"
          />
        </div>
      )}

      {/* Skeleton while loading with shimmer animation */}
      {!showPlaceholder && isLoading && (
        <div className="absolute inset-0 overflow-hidden rounded-lg">
          <Skeleton className="absolute inset-0 w-full h-full bg-gray-200 dark:bg-gray-700" />
        </div>
      )}

      {/* Actual image */}
      {!showPlaceholder && (shouldLoad || isInView) && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? "eager" : "lazy"}
          // @ts-expect-error - React uses camelCase but HTML attribute is lowercase
          fetchpriority={priority ? "high" : undefined}
          decoding={priority ? "sync" : "async"}
          className={cn(
            "w-full h-full object-cover",
            isLoading ? "opacity-0" : "opacity-100",
            "transition-opacity duration-300",
            className
          )}
          style={rotation ? {
            transform: `rotate(${rotation}deg)${scaleOnRotate && (rotation === 90 || rotation === 270) ? ' scale(1.35)' : ''}`
          } : undefined}
          onError={() => setError(true)}
          onLoad={() => setIsLoading(false)}
          {...props}
        />
      )}
    </div>
  );
}
